import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Cookie } from "lucide-react";
import { useSiteSettings } from "../../lib/settings";
import { useI18n } from "../../lib/i18n";

const STORAGE_KEY = "firat-cookie-consent-v2";

const DEFAULT_CONSENT = {
  necessary: true,   // always
  analytics: false,
  marketing: false,
  support: false,
  ts: 0,
};

// Localized copy for the cookie consent UI (TR / EN / AR)
const COOKIE_STRINGS = {
  tr: {
    noticeTitle: "Bu sitede çerezler kullanıyoruz",
    bodyBefore: "Deneyiminizi iyileştirmek, trafiği analiz etmek ve size uygun içerik sunmak için çerezleri kullanıyoruz. Tercihinizi istediğiniz zaman değiştirebilirsiniz. Detaylar için ",
    policyLink: "Çerez Politikası",
    bodyAfter: "'nı inceleyin.",
    rejectAll: "Reddet",
    customize: "Tercihleri Yönet",
    acceptAll: "Tümünü Kabul Et",
    prefsTitle: "Çerez Tercihleri",
    cancel: "İptal",
    save: "Seçimi Kaydet",
    necessaryTitle: "Zorunlu Çerezler",
    necessaryBadge: "Her zaman aktif",
    necessaryDesc: "Oturum yönetimi, güvenlik ve temel işlevler. Devre dışı bırakılamaz.",
    analyticsTitle: "Analitik Çerezler",
    analyticsSuffix: "— Sayfa görüntülemeleri, ziyaretçi kaynakları ve kullanıcı davranışı ölçümü.",
    analyticsEmpty: "Sayfa görüntülemeleri ve ziyaretçi davranışı ölçümü (henüz yapılandırılmadı).",
    marketingTitle: "Pazarlama & Reklam Çerezleri",
    marketingSuffix: "— Reklam performansı ve yeniden hedefleme.",
    marketingEmpty: "Reklam performansı ve yeniden hedefleme (henüz yapılandırılmadı).",
    supportTitle: "Destek Araçları",
    supportSuffix: "— Canlı müşteri destek sohbet widgetı.",
    supportEmpty: "Canlı müşteri destek sohbet widgetı (henüz yapılandırılmadı).",
  },
  en: {
    noticeTitle: "We use cookies on this site",
    bodyBefore: "We use cookies to improve your experience, analyze traffic and deliver relevant content. You can change your preferences at any time. See our ",
    policyLink: "Cookie Policy",
    bodyAfter: " for details.",
    rejectAll: "Reject",
    customize: "Manage Preferences",
    acceptAll: "Accept All",
    prefsTitle: "Cookie Preferences",
    cancel: "Cancel",
    save: "Save Selection",
    necessaryTitle: "Necessary Cookies",
    necessaryBadge: "Always on",
    necessaryDesc: "Session management, security and core functionality. Cannot be disabled.",
    analyticsTitle: "Analytics Cookies",
    analyticsSuffix: "— Page views, traffic sources and user behavior measurement.",
    analyticsEmpty: "Page views and visitor behavior measurement (not yet configured).",
    marketingTitle: "Marketing & Advertising Cookies",
    marketingSuffix: "— Ad performance and retargeting.",
    marketingEmpty: "Ad performance and retargeting (not yet configured).",
    supportTitle: "Support Tools",
    supportSuffix: "— Live customer support chat widget.",
    supportEmpty: "Live customer support chat widget (not yet configured).",
  },
  ar: {
    noticeTitle: "نستخدم ملفات تعريف الارتباط في هذا الموقع",
    bodyBefore: "نستخدم ملفات تعريف الارتباط لتحسين تجربتك وتحليل الحركة وتقديم محتوى مناسب. يمكنك تغيير تفضيلاتك في أي وقت. للمزيد راجع ",
    policyLink: "سياسة ملفات تعريف الارتباط",
    bodyAfter: ".",
    rejectAll: "رفض",
    customize: "إدارة التفضيلات",
    acceptAll: "قبول الكل",
    prefsTitle: "تفضيلات ملفات تعريف الارتباط",
    cancel: "إلغاء",
    save: "حفظ الاختيار",
    necessaryTitle: "ملفات ضرورية",
    necessaryBadge: "مفعّل دائماً",
    necessaryDesc: "إدارة الجلسة والأمان والوظائف الأساسية. لا يمكن تعطيلها.",
    analyticsTitle: "ملفات تحليلية",
    analyticsSuffix: "— قياس مشاهدات الصفحات ومصادر الزوار وسلوك المستخدم.",
    analyticsEmpty: "قياس مشاهدات الصفحات وسلوك الزوار (لم يُهيّأ بعد).",
    marketingTitle: "ملفات التسويق والإعلان",
    marketingSuffix: "— أداء الإعلانات وإعادة الاستهداف.",
    marketingEmpty: "أداء الإعلانات وإعادة الاستهداف (لم يُهيّأ بعد).",
    supportTitle: "أدوات الدعم",
    supportSuffix: "— أداة دردشة الدعم المباشر.",
    supportEmpty: "أداة دردشة الدعم المباشر (لم تُهيّأ بعد).",
  },
};

export const readConsent = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v !== "object") return null;
    return { ...DEFAULT_CONSENT, ...v };
  } catch {
    return null;
  }
};

const writeConsent = (val) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...val, ts: Date.now() }));
    window.dispatchEvent(new CustomEvent("firat:consent-change", { detail: val }));
  } catch { /* private mode */ }
};

const Toggle = ({ checked, onChange, disabled, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-brand-primary" : "bg-white/25"} ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    data-testid={testId}
  >
    <span className={`inline-block h-4 w-4 bg-white rounded-full shadow transform transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
  </button>
);

const CookieBanner = () => {
  const settings = useSiteSettings();
  const { lang } = useI18n();
  const c = COOKIE_STRINGS[lang] || COOKIE_STRINGS.tr;
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false); // detailed prefs view
  const [prefs, setPrefs] = useState(DEFAULT_CONSENT);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      const t = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(t);
    }
    setPrefs(existing);
  }, []);

  // Global helper — invoked by the footer "Çerez Tercihleri" link so users
  // can withdraw / change consent at any time (KVKK/GDPR requires this).
  useEffect(() => {
    const handler = () => {
      const stored = readConsent();
      if (stored) setPrefs(stored);
      setExpanded(true);
      setOpen(true);
    };
    window.addEventListener("firat:open-cookie-prefs", handler);
    return () => window.removeEventListener("firat:open-cookie-prefs", handler);
  }, []);

  const acceptAll = () => {
    const val = { necessary: true, analytics: true, marketing: true, support: true };
    writeConsent(val);
    setPrefs(val);
    setOpen(false);
  };
  const rejectAll = () => {
    const val = { necessary: true, analytics: false, marketing: false, support: false };
    writeConsent(val);
    setPrefs(val);
    setOpen(false);
  };
  const saveSelection = () => {
    writeConsent(prefs);
    setOpen(false);
  };

  const set = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));

  if (!open) return null;

  const analyticsIds = [];
  if (settings.ga4_measurement_id) analyticsIds.push(`Google Analytics 4 (${settings.ga4_measurement_id})`);
  if (settings.google_tag_manager_id) analyticsIds.push(`Google Tag Manager (${settings.google_tag_manager_id})`);
  if (settings.hotjar_id) analyticsIds.push(`Hotjar`);

  const marketingIds = [];
  if (settings.google_ads_id) marketingIds.push(`Google Ads (${settings.google_ads_id})`);
  if (settings.meta_pixel_id) marketingIds.push(`Meta Pixel`);
  if (settings.tiktok_pixel_id) marketingIds.push(`TikTok Pixel`);
  if (settings.linkedin_insight_id) marketingIds.push(`LinkedIn Insight`);

  const supportIds = [];
  if (settings.jivochat_id) supportIds.push(`JivoChat`);
  if (settings.tawk_property_id) supportIds.push(`Tawk.to`);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={
          expanded
            ? "fixed inset-0 z-[95] bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4"
            : "fixed z-[95] bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-full md:max-w-md"
        }
        data-testid="cookie-banner"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
          className={
            expanded
              ? "relative bg-brand-navy text-white w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl"
              : "relative bg-brand-navy text-white w-full shadow-2xl rounded-xl overflow-hidden border border-white/10"
          }
          dir={lang === "ar" ? "rtl" : "ltr"}
        >
          {expanded ? (
            <>
              <div className="flex items-center justify-between p-6 md:p-8 pb-4">
                <h2 className="font-serif text-2xl">{c.prefsTitle}</h2>
                <button
                  type="button"
                  onClick={() => {
                    if (readConsent()) { setOpen(false); setExpanded(false); }
                    else { setExpanded(false); }
                  }}
                  className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                  data-testid="cookie-close"
                >
                  <X size={18} strokeWidth={1.5} />
                </button>
              </div>
              <div className="px-6 md:px-8 space-y-6 pb-6">
                <ConsentRow
                  title={c.necessaryTitle}
                  badge={c.necessaryBadge}
                  desc={c.necessaryDesc}
                  checked={true} disabled testId="consent-necessary"
                />
                <ConsentRow
                  title={c.analyticsTitle}
                  desc={analyticsIds.length ? `${analyticsIds.join(", ")} ${c.analyticsSuffix}` : c.analyticsEmpty}
                  checked={prefs.analytics} onChange={(v) => set("analytics", v)} testId="consent-analytics"
                />
                <ConsentRow
                  title={c.marketingTitle}
                  desc={marketingIds.length ? `${marketingIds.join(", ")} ${c.marketingSuffix}` : c.marketingEmpty}
                  checked={prefs.marketing} onChange={(v) => set("marketing", v)} testId="consent-marketing"
                />
                <ConsentRow
                  title={c.supportTitle}
                  desc={supportIds.length ? `${supportIds.join(", ")} ${c.supportSuffix}` : c.supportEmpty}
                  checked={prefs.support} onChange={(v) => set("support", v)} testId="consent-support"
                />
              </div>
              <div className="p-6 md:p-8 pt-4 flex flex-col-reverse md:flex-row gap-3 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => {
                    if (readConsent()) { setOpen(false); setExpanded(false); }
                    else { setExpanded(false); }
                  }}
                  className="px-5 py-3 border border-white/25 text-[0.7rem] tracking-[0.24em] uppercase hover:bg-white/5 transition-colors"
                  data-testid="cookie-cancel"
                >
                  {c.cancel}
                </button>
                <button type="button" onClick={saveSelection} className="px-6 py-3 bg-white text-brand-navy text-[0.7rem] tracking-[0.24em] uppercase font-medium hover:bg-white/90 transition-colors" data-testid="cookie-save-selection">{c.save}</button>
              </div>
            </>
          ) : (
            <div className="p-5 md:p-6">
              <div className="flex items-start gap-3.5">
                <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Cookie size={18} strokeWidth={1.5} />
                </span>
                <div className="flex-1 min-w-0">
                  <h2 className="font-serif text-lg md:text-xl leading-tight">{c.noticeTitle}</h2>
                  <p className="mt-2 text-[0.82rem] text-white/75 leading-relaxed">
                    {c.bodyBefore}
                    <Link to="/legal/cerez" className="underline underline-offset-4">{c.policyLink}</Link>{c.bodyAfter}
                  </p>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <button type="button" onClick={acceptAll} className="flex-1 min-w-[8rem] px-4 py-2.5 bg-white text-brand-navy text-[0.66rem] tracking-[0.2em] uppercase font-medium hover:bg-white/90 transition-colors" data-testid="cookie-accept-all">{c.acceptAll}</button>
                <button type="button" onClick={rejectAll} className="flex-1 min-w-[6rem] px-4 py-2.5 border border-white/25 text-[0.66rem] tracking-[0.2em] uppercase hover:bg-white/5 transition-colors" data-testid="cookie-reject-all">{c.rejectAll}</button>
                <button type="button" onClick={() => setExpanded(true)} className="w-full px-4 py-2 text-[0.62rem] tracking-[0.18em] uppercase text-white/60 hover:text-white transition-colors" data-testid="cookie-customize">{c.customize}</button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const ConsentRow = ({ title, badge, desc, checked, onChange, disabled, testId }) => (
  <div className="border-b border-white/10 pb-6 last:border-b-0 last:pb-0">
    <div className="flex items-start gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <h3 className="font-medium text-white">{title}</h3>
          {badge ? (
            <span className="text-[0.62rem] tracking-[0.18em] uppercase px-2.5 py-0.5 rounded-full bg-white/10 text-white/70 border border-white/15">{badge}</span>
          ) : null}
        </div>
        <p className="mt-2 text-sm text-white/70 leading-relaxed">{desc}</p>
      </div>
      <Toggle checked={checked} onChange={onChange || (() => {})} disabled={disabled} testId={testId} />
    </div>
  </div>
);

export default CookieBanner;
